import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Skull, Trophy, Hourglass, Crown } from "lucide-react";
import api from "@/lib/api";
import { PageHeader, Loader, Empty, Pagination } from "@/components/Page";
import { CLASS_META, SKILL_META, SKILLS } from "@/lib/game";

export function RetiredCard({ g }) {
  const meta = CLASS_META[g.cls] || {};
  const I = meta.icon;
  const stats = g.peak_skills || g.final_skills || g.skills;
  return (
    <div data-testid={`retired-card-${g.id}`} className="bg-zinc-950 border border-zinc-700 p-4 relative overflow-hidden">
      <div className="absolute top-0 right-0 px-2 py-0.5 text-[10px] uppercase tracking-widest bg-zinc-800 text-zinc-400 border-l border-b border-zinc-600">Retired</div>
      <div className="flex items-center gap-2">{I && <I className="text-zinc-400" size={20} />}
        <span className="text-[11px] uppercase tracking-widest text-zinc-500">{meta.tag}</span></div>
      <h3 className="font-cinzel text-lg text-zinc-200 tracking-wide mt-2 uppercase truncate">{g.name}</h3>
      <div className="flex items-center gap-4 mt-1.5 text-xs text-zinc-500">
        <span className="flex items-center gap-1"><Hourglass size={12} /> Retired at {g.retire_age || 35}</span>
        <span className="flex items-center gap-1"><Trophy size={12} /> {g.rating}</span>
        <span>{g.wins}W / {g.losses}L</span>
      </div>
      <div className="grid grid-cols-3 gap-x-3 gap-y-1.5 mt-4">
        <div className="col-span-3 text-[10px] uppercase tracking-widest text-zinc-600 mb-0.5">Peak Stats</div>
        {SKILLS.map((s) => (
          <div key={s} className="flex justify-between text-[11px]">
            <span className="text-zinc-600">{SKILL_META[s].label.slice(0, 3)}</span>
            <span className="text-zinc-300 tabular-nums">{stats?.[s] ?? 0}</span>
          </div>
        ))}
      </div>
      <p className="text-[11px] text-zinc-600 mt-3 border-t border-zinc-800 pt-2">
        Career: {g.wins + g.losses} battles · Potential {g.potential_grade}
      </p>
    </div>
  );
}

function MyRetired() {
  const [retired, setRetired] = useState(null);
  useEffect(() => { api.get("/gladiators").then((r) => setRetired(r.data.retired)).catch(() => setRetired([])); }, []);
  if (retired === null) return <Loader />;
  if (retired.length === 0) return <Empty label="No retired gladiators yet. Fighters retire at age 35 — speed up time to see the full lifecycle." />;
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
      {retired.map((g) => <RetiredCard key={g.id} g={g} />)}
    </div>
  );
}

function HallOfRetired() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [page, setPage] = useState(1);

  useEffect(() => {
    setData(null);
    api.get(`/retired/public?page=${page}`).then((r) => setData(r.data)).catch(() => setData({ hall: [], pages: 1, total: 0, page: 1 }));
  }, [page]);

  if (data === null) return <Loader label="Summoning the legends…" />;
  if (data.total === 0) return <Empty label="No gladiators have retired across the arena yet." />;

  return (
    <div>
      <p className="text-zinc-500 text-sm mb-4">{data.total} retired gladiators across all ludi · ranked by highest rating ever achieved</p>
      <div className="border border-amber-900/30 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-zinc-950 text-[11px] uppercase tracking-widest text-zinc-500 border-b border-amber-900/30">
              <th className="text-left px-4 py-3">#</th>
              <th className="text-left px-4 py-3">Gladiator</th>
              <th className="text-left px-4 py-3 hidden sm:table-cell">Class</th>
              <th className="text-left px-4 py-3 hidden md:table-cell">Lanista</th>
              <th className="text-right px-4 py-3">Peak Rating</th>
              <th className="text-right px-4 py-3 hidden sm:table-cell">W/L</th>
              <th className="text-right px-4 py-3 hidden md:table-cell">Potential</th>
            </tr>
          </thead>
          <tbody>
            {data.hall.map((r) => {
              const meta = CLASS_META[r.cls] || {};
              return (
                <tr key={r.id} data-testid={`hall-row-${r.id}`} className="border-b border-zinc-900 hover:bg-zinc-900/40">
                  <td className="px-4 py-3 font-cinzel text-amber-400 tabular-nums">{r.rank}</td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center gap-1.5">
                      {r.rank === 1 && <Crown size={13} className="text-amber-400 shrink-0" />}
                      <button data-testid={`hall-name-${r.id}`} onClick={() => navigate(`/gladiator/${r.id}`)}
                        className="text-zinc-100 hover:text-amber-400 transition-colors">{r.name}</button>
                    </span>
                  </td>
                  <td className="px-4 py-3 hidden sm:table-cell"><span className={meta.color}>{r.cls}</span></td>
                  <td className="px-4 py-3 hidden md:table-cell">
                    <button className="text-zinc-400 hover:text-amber-300 underline" onClick={() => navigate(`/profile/${r.owner_id}`)}>{r.owner_name}</button>
                  </td>
                  <td className="px-4 py-3 text-right font-semibold text-amber-300 tabular-nums">{r.best_rating}</td>
                  <td className="px-4 py-3 text-right text-zinc-400 hidden sm:table-cell">{r.wins}/{r.losses}</td>
                  <td className="px-4 py-3 text-right text-violet-400 hidden md:table-cell">{r.potential_grade}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {data.pages > 1 && (
        <Pagination page={page} totalPages={data.pages} onChange={setPage} testid="hall" className="mt-5" />
      )}
    </div>
  );
}

export function RetiredContent() {
  const [tab, setTab] = useState("mine");
  return (
    <div>
      <div className="flex gap-2 mb-6 border-b border-zinc-800">
        {[["mine", "My Ludus"], ["hall", "Hall of the Retired"]].map(([id, label]) => (
          <button key={id} data-testid={`retired-tab-${id}`} onClick={() => setTab(id)}
            className={`px-4 py-2 text-sm font-cinzel uppercase tracking-wide border-b-2 -mb-px transition-colors ${
              tab === id ? "border-amber-500 text-amber-300" : "border-transparent text-zinc-500 hover:text-amber-300"}`}>
            {label}
          </button>
        ))}
      </div>
      {tab === "mine" ? <MyRetired /> : <HallOfRetired />}
    </div>
  );
}

export default function Retired() {
  return (
    <div>
      <PageHeader title="Retired Gladiators" subtitle="Honoring those who served the arena" icon={Skull} />
      <RetiredContent />
    </div>
  );
}
